// src/components/PanoramicViewTab.js - REFACTORED FOR BASE64
import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TextField,
  Grid,
  Button,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormContext } from '../context/FormContext';
import { fileToBase64 } from '../utils/fileUtils'; // Import the new utility

const PAN_VIEW_COMMENTS = {
  options: [
    'Present teeth are periodontally involved and shows bone loss',
    'Missing crown portion irt',
    'Deep dental caries irt',
    'Periapical lesion/cyst irt',
    'Distoproximal / mesioproximal dental/secondary caries irt',
    'Root stump irt',
    'Grossly destructed irt',
    'Fixed prosthetic crown irt /  Fixed prosthesis irt.',
    'Coronal restoration irt occlusal aspect of',
    'Distoproximal / mesioproximal restoration irt',
    'Root canal treated irt',
    'Adjacent teeth show no pathology',
    'Distally / Mesially tipping irt',
    'Impaction irt',
    'Vertically impacted / Horizontal impacted',
    'Tooth fracture',
    'Generalized attrition',
    'Over retained irt',
    "Completely edentulous maxilla",
    "Completely edentulous mandible",
    "Implant placed irt",
    'Other',
  ],
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const PanoramicViewTab = () => {
  const { state, actions } = useFormContext();

  // The panoramicFile properties now hold an object: { base64, name, type }
  const localState = {
    panoramicFile1: state.panoramicViewData.panoramicFile1,
    panoramicFile2: state.panoramicViewData.panoramicFile2,
    selectedComments: state.panoramicViewUIState.selectedComments,
    otherComments: state.panoramicViewUIState.otherComments,
  };

  const combineComments = () => {
    const explicit = localState.selectedComments.filter((c) => c !== 'Other');
    if (localState.selectedComments.includes('Other') && localState.otherComments.trim()) {
      explicit.push(localState.otherComments.trim());
    }
    return explicit.join('\n');
  };

  // This effect remains crucial for syncing all panoramic data to the main context.
  useEffect(() => {
    const dataToSend = {
      panoramicFile1: localState.panoramicFile1,
      panoramicFile2: localState.panoramicFile2,
      comments: combineComments(),
    };

    if (
      dataToSend.panoramicFile1 !== state.panoramicViewData.panoramicFile1 ||
      dataToSend.panoramicFile2 !== state.panoramicViewData.panoramicFile2 ||
      dataToSend.comments !== state.panoramicViewData.comments
    ) {
      actions.setPanoramicData(dataToSend);
    }
  }, [localState.panoramicFile1, localState.panoramicFile2, localState.selectedComments, localState.otherComments, actions, state.panoramicViewData]);

  const handleFileChange = async (index) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        console.log(`PanoramicViewTab: File ${index} selected:`, file.name, file.size);

        try {
            const base64 = await fileToBase64(file);
            const fileData = { base64, name: file.name, type: file.type };

            if (index === 1) {
                actions.setPanoramicData({ ...state.panoramicViewData, panoramicFile1: fileData });
                actions.updatePanoramicUIState({ preview1: base64 });
            } else {
                actions.setPanoramicData({ ...state.panoramicViewData, panoramicFile2: fileData });
                actions.updatePanoramicUIState({ preview2: base64 });
            }
        } catch (error) {
            console.error("Error converting file to Base64:", error);
            // Optionally, show an error to the user
        }
    };
    input.click();
  };

  const handleRemoveFile = (index) => {
    if (index === 1) {
      actions.setPanoramicData({ ...state.panoramicViewData, panoramicFile1: null });
      actions.updatePanoramicUIState({ preview1: null });
    } else if (index === 2) {
      actions.setPanoramicData({ ...state.panoramicViewData, panoramicFile2: null });
      actions.updatePanoramicUIState({ preview2: null });
    }
  };

  const handleCommentChange = (event) => {
    const { target: { value } } = event;
    const newSelectedComments = typeof value === 'string' ? value.split(',') : value;
    actions.updatePanoramicUIState({ selectedComments: newSelectedComments });
  };

  const handleOtherCommentsChange = (event) => {
    actions.updatePanoramicUIState({ otherComments: event.target.value });
  };

  const renderFilePicker = (label, idx, fileData) => {
    const preview = fileData ? fileData.base64 : null;

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">{label}</Typography>
                    {preview && (
                        <IconButton size="small" color="error" onClick={() => handleRemoveFile(idx)} title="Remove file">
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>

                {preview ? (
                    <Box mb={2} sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                        <img
                            src={preview}
                            alt={label}
                            style={{ width: '100%', height: '200px', objectFit: 'contain', display: 'block' }}
                        />
                    </Box>
                ) : (
                    <Box mb={2} sx={{ border: '2px dashed #ccc', borderRadius: 1, height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="body2" color="textSecondary">No image selected</Typography>
                    </Box>
                )}

                <Button
                    variant={preview ? "outlined" : "contained"}
                    component="label"
                    fullWidth
                    color={preview ? "secondary" : "primary"}
                    onClick={() => handleFileChange(idx)} // Use programmatic click
                >
                    {preview ? 'Replace Image' : 'Select Image'}
                    {/* The actual file input is now created programmatically */}
                </Button>

                {fileData && (
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                        {fileData.name}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
  }

  if (!state.patientData) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="textSecondary">
          Please load patient data first from the sidebar.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Panoramic View
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          {renderFilePicker(
            'Panoramic View Image 1',
            1,
            localState.panoramicFile1
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderFilePicker(
            'Panoramic View Image 2',
            2,
            localState.panoramicFile2
          )}
        </Grid>
      </Grid>

      <FormControl fullWidth sx={{ mt: 3 }}>
        <InputLabel>Comments on Panoramic View</InputLabel>
        <Select
          multiple
          value={localState.selectedComments}
          onChange={handleCommentChange}
          input={<OutlinedInput label="Comments on Panoramic View" />}
          renderValue={(selected) => selected.join(', ')}
          MenuProps={MenuProps}
        >
          {PAN_VIEW_COMMENTS.options.map((name) => (
            <MenuItem key={name} value={name}>
              <Checkbox checked={localState.selectedComments.indexOf(name) > -1} />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {localState.selectedComments.includes('Other') && (
        <TextField
          fullWidth
          label="Please specify other comments"
          multiline
          rows={3}
          value={localState.otherComments}
          onChange={handleOtherCommentsChange}
          sx={{ mt: 2 }}
          placeholder="Enter your custom comments here..."
        />
      )}
    </Box>
  );
};

export default PanoramicViewTab;
